import type { FibPaymentResult, FibStatusResult } from '@erp/shared';
import { CheckCircle2, CreditCard, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Button } from '../../components/ui/index.js';
import { publicApi } from '../../lib/publicApi.js';

/** Pay a storefront order with FIB: create payment → show QR/code → poll status.
 *  `autoStart` immediately creates the payment (used inline right after checkout);
 *  `onPaid` fires once the poll confirms payment. */
export function FibPayBox({ slug, orderNumber, autoStart, onPaid }: { slug: string; orderNumber: string; autoStart?: boolean; onPaid?: () => void }): JSX.Element {
  const { t } = useTranslation('cms');
  const [pay, setPay] = useState<FibPaymentResult | null>(null);
  const [paid, setPaid] = useState(false);
  const [busy, setBusy] = useState(false);
  const [started, setStarted] = useState(false);

  const create = async (): Promise<void> => {
    setBusy(true);
    try {
      const r = await publicApi().post<FibPaymentResult>(`/${slug}/fib/pay`, { orderNumber });
      setPay(r.data);
    } catch (e) {
      // The server refuses to charge an already-paid order — that is the PAID
      // state arriving late (another tab, the callback), not a failure.
      const cause = (e as { response?: { data?: { cause?: { code?: string } } } })?.response?.data?.cause?.code;
      if (cause === 'fib.already_paid') { setPaid(true); onPaid?.(); return; }
      toast.error(t('fib.failed', { defaultValue: 'Could not start the FIB payment.' }));
    } finally { setBusy(false); }
  };

  // Kick off the payment immediately when embedded inline in the checkout flow.
  useEffect(() => {
    if (autoStart && !started) { setStarted(true); void create(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoStart, started]);

  useEffect(() => {
    if (!pay || paid) return undefined;
    const id = window.setInterval(() => {
      void publicApi().get<FibStatusResult>(`/${slug}/fib/status/${pay.paymentId}`)
        .then((r) => { if (r.data.paid) { setPaid(true); window.clearInterval(id); onPaid?.(); } })
        .catch(() => undefined);
    }, 4000);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pay, paid, slug]);

  if (paid) {
    return (
      <div className="flex items-center gap-3 rounded-2xl border border-success/40 bg-success/10 p-4 text-success">
        <CheckCircle2 className="h-6 w-6 flex-none" aria-hidden />
        <span className="font-semibold">{t('fib.paid', { defaultValue: 'Payment received — thank you!' })}</span>
      </div>
    );
  }

  if (!pay) {
    return (
      <Button onClick={create} loading={busy} size="lg">
        <CreditCard className="h-4 w-4" aria-hidden />{t('fib.pay', { defaultValue: 'Pay with FIB' })}
      </Button>
    );
  }

  const qrSrc = pay.qrCode ? (pay.qrCode.startsWith('data:') ? pay.qrCode : `data:image/png;base64,${pay.qrCode}`) : null;
  return (
    <div className="flex flex-col items-center gap-3 rounded-2xl border border-border bg-bg-subtle p-6 text-center sm:flex-row sm:items-start sm:text-start">
      {qrSrc && <img src={qrSrc} alt="FIB QR" className="h-40 w-40 flex-none rounded-lg bg-white p-2" />}
      <div className="flex flex-col gap-2">
        <h3 className="font-semibold">{t('fib.scanTitle', { defaultValue: 'Scan with the FIB app' })}</h3>
        <p className="text-sm text-fg-muted">{t('fib.scanHint', { defaultValue: 'Open the FIB app, scan the code, and approve the payment.' })}</p>
        {pay.readableCode && (
          <p className="text-sm">{t('fib.code', { defaultValue: 'Or enter code' })}: <span className="font-mono font-bold tracking-wider">{pay.readableCode}</span></p>
        )}
        {pay.paymentLink && (
          <a href={pay.paymentLink} className="w-fit rounded-full bg-brand px-4 py-2 text-sm font-semibold text-brand-contrast transition-[transform,opacity] duration-150 hover:opacity-90 active:scale-[0.97]">
            {t('fib.openApp', { defaultValue: 'Open FIB app' })}
          </a>
        )}
        <span className="inline-flex items-center gap-1.5 text-xs text-fg-muted"><Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />{t('fib.waiting', { defaultValue: 'Waiting for payment…' })}</span>
      </div>
    </div>
  );
}
